import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color } from '@tiptap/extension-text-style';

/* ---- Preset colours ---- */
const COLOURS = [
  { label: 'Default', value: null },
  { label: 'Navy', value: '#072040' },
  { label: 'Gold', value: '#f0a500' },
  { label: 'Blue', value: '#599BE3' },
  { label: 'Dark', value: '#212529' },
  { label: 'Grey', value: '#6c757d' },
  { label: 'Green', value: '#91CF9B' },
  { label: 'Red', value: '#dc3545' },
];

/* ---- Dropdown component ---- */
const ToolbarDropdown = ({ label, options, currentValue, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const activeOption = options.find(o => o.value === currentValue);

  return (
    <div className="tiptap-dropdown" ref={ref}>
      <button
        type="button"
        className="tiptap-btn tiptap-dropdown-trigger"
        onClick={() => setOpen(!open)}
        title={label}
      >
        <span className="tiptap-dropdown-label">{activeOption?.label || label}</span>
        <span className="tiptap-dropdown-arrow">▾</span>
      </button>
      {open && (
        <div className="tiptap-dropdown-menu">
          {options.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className={`tiptap-dropdown-item ${opt.value === currentValue ? 'is-active' : ''}`}
              onClick={() => { onSelect(opt.value); setOpen(false); }}
            >
              {opt.swatch && (
                <span className="tiptap-colour-swatch" style={{ background: opt.swatch }} />
              )}
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---- Menu bar ---- */
const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const currentColour = editor.getAttributes('textStyle').color || null;

  const colourOptions = COLOURS.map(c => ({
    ...c,
    swatch: c.value || '#212529',
  }));

  // Determine which heading level is active (if any)
  const activeHeading = [1, 2, 3].find(level => editor.isActive('heading', { level }));

  return (
    <div className="tiptap-toolbar">
      {/* Bold / Italic */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold"
      >
        B
      </button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic"
      >
        <em>I</em>
      </button>

      <span className="tiptap-separator" />

      {/* Headings */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title="Heading 2"
      >
        H2
      </button>

      <span className="tiptap-separator" />

      {/* Lists */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet list"
      >
        &bull;
      </button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list"
      >
        1.
      </button>

      <span className="tiptap-separator" />

      {/* Colour dropdown */}
      <ToolbarDropdown
        label="Colour"
        options={colourOptions}
        currentValue={currentColour}
        onSelect={(colour) => {
          if (colour) {
            editor.chain().focus().setColor(colour).run();
          } else {
            editor.chain().focus().unsetColor().run();
          }
        }}
      />

      <span className="tiptap-separator" />

      {/* Clear formatting */}
      <button
        type="button"
        className="tiptap-btn"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        title="Clear formatting"
      >
        ✕
      </button>

      <span className="tiptap-separator" />

      {/* Undo / Redo */}
      <button
        type="button"
        className="tiptap-btn"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        ↩
      </button>
      <button
        type="button"
        className="tiptap-btn"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        ↪
      </button>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceHtml, setSourceHtml] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      TextStyle,
      Color,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        spellcheck: 'true',
        lang: 'en-AU',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. loading new content)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const handleToggleSource = () => {
    if (sourceMode) {
      // Switching back to visual: apply edited HTML
      if (editor) {
        editor.commands.setContent(sourceHtml, false);
        onChange(sourceHtml);
      }
      setSourceMode(false);
    } else {
      // Switching to source: capture current HTML
      setSourceHtml(editor ? editor.getHTML() : value || '');
      setSourceMode(true);
    }
  };

  return (
    <div className="tiptap-editor">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          {!sourceMode && <MenuBar editor={editor} />}
        </div>
        <button
          type="button"
          className={`tiptap-btn tiptap-source-toggle ${sourceMode ? 'is-active' : ''}`}
          onClick={handleToggleSource}
          title={sourceMode ? 'Visual editor' : 'View HTML source'}
          style={{ margin: '4px 6px', fontSize: '11px', fontFamily: 'monospace' }}
        >
          &lt;/&gt;
        </button>
      </div>
      {sourceMode ? (
        <textarea
          className="tiptap-source-textarea"
          value={sourceHtml}
          onChange={(e) => setSourceHtml(e.target.value)}
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} className="tiptap-content" placeholder={placeholder} />
      )}
    </div>
  );
};

export default RichTextEditor;
